import React from "react";
import { Info } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { SignInButton, UserButton, useClerk, useUser } from "@clerk/clerk-react";
import { SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { clearDemoMode, getDemoSessionId, isDemoModeScoped } from "@/lib/demoMode";
import { readFlyingTextEnabled, writeFlyingTextEnabled } from "@/lib/flyingTextEnabled";

// WorkspaceHeader.tsx
export const HEADER_CONTENT_MAX_WIDTH = 1280; // or whatever you use

export default function WorkspaceHeader() {
  const { isSignedIn, user } = useUser();
  const { signOut } = useClerk();
  const { open } = useSidebar();
  const location = useLocation();
  const isBetaSplashActive = !isSignedIn && location.pathname === "/";
  const isDemo =
    isSignedIn &&
    isDemoModeScoped({
      userId: user?.id,
      email: user?.primaryEmailAddress?.emailAddress,
    });
  const [demoSigningOut, setDemoSigningOut] = React.useState(false);
  const calceraClickTimesRef = React.useRef<number[]>([]);
  const handleCalceraClick = (e: React.MouseEvent) => {
    const now = Date.now();

    // keep clicks within last 2 seconds
    calceraClickTimesRef.current =
      calceraClickTimesRef.current.filter((t) => now - t < 2000);

    calceraClickTimesRef.current.push(now);

    // 🎮 Easter egg trigger: 3 clicks in 2s
    if (calceraClickTimesRef.current.length >= 3) {
      calceraClickTimesRef.current = [];

      // prevent normal navigation
      e.preventDefault();
      e.stopPropagation();

      window.dispatchEvent(
        new CustomEvent("workspace:open-tab", {
          detail: {
            type: "calcera-ball",
            forceNew: true,
          },
        }),
      );

      return;
    }

    // otherwise allow normal Link navigation
  };


  const [flyingTextEnabled, setFlyingTextEnabled] = React.useState<boolean>(() =>
    readFlyingTextEnabled({ defaultValue: true }),
  );

  React.useEffect(() => {
    writeFlyingTextEnabled(flyingTextEnabled);
  }, [flyingTextEnabled]);

  const handleDemoLogout = React.useCallback(async () => {
    if (demoSigningOut) return;
    setDemoSigningOut(true);
    try {
      const sid = getDemoSessionId();
      try {
        // Clear demo-local data & workspace layout so the next viewer starts clean.
        if (sid) {
          window.localStorage.removeItem(`calcera:demo:${sid}:notes:snapshot`);
          window.localStorage.removeItem(`calcera:demo:${sid}:tasks:snapshot`);
        }
        window.localStorage.removeItem("workspace:layout:v4");
        window.localStorage.removeItem("workspace:layout");
      } catch {
        // ignore storage errors
      }

      clearDemoMode();

      try {
        await signOut({ redirectUrl: "/" });
      } catch {
        await signOut();
      }
    } finally {
      setDemoSigningOut(false);
    }
  }, [demoSigningOut, signOut]);

  return (
    <div
      className="
        relative z-[50] flex h-[56px] w-full shrink-0 items-center
        overflow-hidden
        bg-[rgba(20,0,45,0.9)]
        backdrop-blur-xl
        border-b border-purple-400/25
        shadow-[0_0_15px_rgba(140,64,255,0.8)]
      "
    >
      {/* Chrome / HUD lines */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-purple-300/80 to-transparent opacity-90" />
      <div
        className="
          absolute top-[1px] left-0 right-0 h-[2px]
          bg-gradient-to-r from-purple-500/0 via-purple-400/60 to-purple-500/0
          blur-[2px]
          opacity-[0.75]
          pointer-events-none
        "
      />
      <div
        className="
          absolute bottom-0 left-0 right-0 h-[3px]
          bg-gradient-to-r from-transparent via-purple-500/55 to-transparent
          blur-[8px]
          opacity-[0.85]
          pointer-events-none
        "
      />

      <TooltipProvider delayDuration={500}>
        <div className="relative grid h-full w-full grid-cols-[auto,1fr,auto] items-center px-4">
          {/* Left – sidebar + HUD toggle */}
          <div className="flex items-center gap-3">
            {!isBetaSplashActive && (
              <>
                <SidebarTrigger
                  className={`
                flex h-8 w-8 items-center justify-center
                rounded-full
                border
                bg-gradient-to-b from-white/8 via-purple-900/50 to-black/60
                text-purple-100
                backdrop-blur-md
                shadow-[0_0_10px_rgba(0,0,0,0.8)]
                transition-all duration-300
                hover:border-purple-100/90
                hover:text-white
                hover:shadow-[0_0_16px_rgba(192,132,252,0.9)]
                hover:bg-gradient-to-b hover:from-purple-500/40 hover:via-purple-700/60 hover:to-black/70
                active:scale-95
                ${open ? `
                  border-purple-200
                  text-white
                  shadow-[0_0_20px_rgba(192,132,252,1)]
                  bg-gradient-to-b from-purple-500/60 via-purple-700/70 to-black/70
                ` : "border-purple-300/40"}
              `}
                />

                {/* <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center">
                      <Switch
                        checked={flyingTextEnabled}
                        onCheckedChange={(value) => setFlyingTextEnabled(Boolean(value))}
                        aria-label="Toggle F.U.N. (Flying User Nodes)"
                        className="
                        h-[22px] w-[44px]
                        relative
                        rounded-full
                        border border-purple-300/40
                        bg-gradient-to-b from-white/8 via-purple-900/50 to-black/60
                        shadow-[0_0_10px_rgba(0,0,0,0.8)]
                        transition-all duration-300

                        hover:border-purple-100/90
                        hover:shadow-[0_0_16px_rgba(192,132,252,0.9)]
                        hover:bg-gradient-to-b hover:from-purple-500/40 hover:via-purple-700/60 hover:to-black/70

                        data-[state=checked]:shadow-[0_0_20px_rgba(192,132,252,1)]
                        data-[state=checked]:border-purple-200/80

                        [&>span]:absolute [&>span]:top-[3px] [&>span]:left-[4px]
                        [&>span]:h-[16px] [&>span]:w-[16px]
                        [&>span]:rounded-full
                        [&>span]:bg-white
                        [&>span]:transition-all duration-300
                        [&>span]:shadow-[0_0_8px_rgba(168,85,247,0.9)]
                        [&>span]:top-[2px] [&>span]:left-[2px]

                        data-[state=checked]:[&>span]:translate-x-[22px]
                    "
                      />

                    </div>
                  </TooltipTrigger>

                  <TooltipContent
                    side="right"
                    align="center"
                    className="px-2 py-1 text-xs leading-tight"
                  >
                    <p className="whitespace-pre-line">
                      {flyingTextEnabled
                        ? "Disable F.U.N.\n(Flying User Nodes)"
                        : "Enable F.U.N.\n(Flying User Nodes)"}
                    </p>
                  </TooltipContent>
                </Tooltip> */}
              </>
            )}
          </div>


        {/* Center - logo (TRUE center, independent of sides) */}
        <div
          id="calcera-header-center"
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        >
          <div className="pointer-events-auto flex items-center gap-2">
            <Link
              to="/"
              onClick={handleCalceraClick}
              className="
                cursor-pointer
                select-none
                text-[22px]
                font-bold
                tracking-[0.36em]
                uppercase
                text-white
                leading-none
                drop-shadow-[0_0_4px_rgba(255,255,255,0.95)]
                drop-shadow-[0_0_10px_rgba(200,160,255,0.85)]
                drop-shadow-[0_0_22px_rgba(160,120,255,0.65)]
                transition-all duration-200
                hover:text-purple-200
              "
            >
              C A L C E R A
            </Link>

            <span className="text-purple-300/70 text-xl">/</span>

            <span className="text-[12px] font-medium uppercase tracking-[0.2em] text-purple-300/85 translate-y-[1px]">
              Beta
            </span>
          </div>
        </div>


          {/* Right – actions & auth */}
          <div className="flex items-center justify-end gap-2">
            <div className="flex items-center gap-2">
              {/* About pill */}
              <Link
                to="/AboutMe"
                className="
                  flex h-8 items-center gap-1.5
                  rounded-full
                  border border-purple-300/40
                  bg-gradient-to-b from-white/8 via-purple-900/50 to-black/60
                  px-4
                  text-[12px] font-semibold uppercase text-purple-100
                  backdrop-blur-md
                  shadow-[0_0_10px_rgba(0,0,0,0.8)]
                  transition-all duration-300

                  hover:text-white
                  hover:border-purple-100/90
                  hover:shadow-[0_0_16px_rgba(192,132,252,0.9)]
                  hover:bg-gradient-to-b hover:from-purple-500/40 hover:via-purple-700/60 hover:to-black/70

                  active:scale-95
                "
              >
                <Info className="h-3.5 w-3.5" />
                <span>About</span>
              </Link>


              {/* Auth – login or avatar */}
              {!isSignedIn && (
                <SignInButton mode="modal">
                  <button
                    type="button"
                    className="
                      flex h-8 items-center justify-center
                      rounded-full border border-purple-300/40
                      bg-gradient-to-b from-white/8 via-purple-900/50 to-black/60
                      px-5
                      text-[12px] font-semibold uppercase text-purple-100
                      shadow-[0_0_10px_rgba(0,0,0,0.8)]
                      backdrop-blur-md
                      transition-all duration-300

                      hover:text-white
                      hover:border-purple-100/90
                      hover:shadow-[0_0_16px_rgba(192,132,252,0.9)]
                      hover:bg-gradient-to-b hover:from-purple-500/40 hover:via-purple-700/60 hover:to-black/70

                      active:scale-95
                    "
                  >
                    Login
                  </button>
                </SignInButton>
              )}

              {isSignedIn && isDemo ? (
                <button
                  type="button"
                  onClick={handleDemoLogout}
                  disabled={demoSigningOut}
                  className="
                    flex h-8 items-center justify-center
                    rounded-full border border-purple-300/40
                    bg-gradient-to-b from-white/8 via-purple-900/50 to-black/60
                    px-5
                    text-[12px] font-semibold uppercase text-purple-100
                    shadow-[0_0_10px_rgba(0,0,0,0.8)]
                    backdrop-blur-md
                    transition-all duration-300

                    hover:text-white
                    hover:border-purple-100/90
                    hover:shadow-[0_0_16px_rgba(192,132,252,0.9)]
                    hover:bg-gradient-to-b hover:from-purple-500/40 hover:via-purple-700/60 hover:to-black/70

                    active:scale-95
                    disabled:opacity-60
                    disabled:cursor-not-allowed
                  "
                  title="Log out of demo mode"
                >
                  {demoSigningOut ? "Logging out…" : "Logout"}
                </button>
              ) : null}

              {isSignedIn && !isDemo && (                
                <UserButton
                  afterSignOutUrl="/"
                  appearance={{
                    elements: {
                      userButtonTrigger: `
                        flex h-8 w-8 items-center justify-center
                        rounded-full p-0
                        border border-purple-200/60
                        bg-black/40
                        shadow-[0_0_10px_rgba(0,0,0,0.8)]
                        transition-all duration-300
                        hover:border-purple-100/90
                        hover:shadow-[0_0_16px_rgba(192,132,252,0.9)]
                      `,
                      userButtonAvatarBox: "h-7 w-7 w-7 rounded-full overflow-hidden",
                    },
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </TooltipProvider>
    </div>
  );
}
